import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout, Step } from "./primitives.js";

export const meta = {
  id: "cluster-expansion",
  title: "Cluster expansion (DFS + LLM + classifier)",
  category: "Workflows",
  lastUpdated: "2026-06-12",
  description: "How to create a cluster + expand it with verified-volume keywords. Layer-2 pipeline: DataForSEO seeds → LLM proposes → classifier flags better-fit clusters.",
  relatedGuides: ["produce-article", "task-management"],
  readTimeMin: 6,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Cluster expansion adds verified-volume keywords to a cluster's backlog. It stacks three signals: DataForSEO related keywords (real volume + intent), Claude Sonnet creativity (slot terms into intents + funnel stages + pillar candidacy), and a Haiku classifier (flag better-fit clusters + propose new clusters when nothing existing fits).
      </Lead>

      <Callout kind="tip">
        Each "Find more" pulls up to <strong>25 keywords</strong> by default (raised from 12 on 2026-06-12) from a 150-candidate DataForSEO pool, and dedupes against what's already in the cluster — so repeated clicks accumulate without repeats. Cost per run ≈ <strong>$0.10–$0.15</strong> (DFS + LLM + ~$0.0001/suggestion classifier). Ceiling per call is 40 keywords / 500 DFS candidates if we ever raise it further.
      </Callout>

      <H2>Creating a cluster first</H2>
      <P>
        Expansion needs a cluster to seed from, so to start a new topic from scratch use <strong>New cluster</strong> on the <AppLink to="pipeline">Pipeline</AppLink> header. Give it a Hebrew + English name (the id auto-derives), and set a <strong>main / pillar keyword</strong> — that's the term DataForSEO seeds from when you Expand. You can also create a cluster inline while adding a keyword: pick <C>+ Create new cluster…</C> in the cluster dropdown, and the keyword you typed prefills the pillar.
      </P>
      <Callout kind="tip">
        Ways a cluster comes into being: <strong>New cluster</strong> button (from scratch), <C>+ Create new cluster…</C> in Add-keyword (around a main keyword) or in the <strong>CSV import</strong> confirm step (import a whole list as one new cluster), or the <strong>Proposed new clusters</strong> section below (the classifier proposes neighbors while expanding).
      </Callout>

      <H2>Importing a CSV or Excel file into clusters</H2>
      <P>The import wizard takes both <C>.csv</C> and <C>.xlsx</C> (a Google Sheets / Excel download works directly — its first sheet is used).</P>
      <Ul>
        <Li><strong>Into one new cluster</strong> — in the import wizard's Confirm step, pick <C>+ Create new cluster…</C> as the default cluster. The whole list lands in that fresh cluster.</Li>
        <Li><strong>Spread across existing clusters</strong> — tick <strong>Auto-classify into best-fit clusters</strong> in the Confirm step. After insert, the Haiku classifier routes each unclustered keyword to the most appropriate existing cluster (≥60% confidence, ~$0.0001 + ~0.3s each). Keywords that fit nothing surface as <em>proposed new clusters</em> — it never auto-creates them, so create those manually and re-classify.</Li>
        <Li><strong>By a cluster column</strong> — if your CSV has a cluster/topic column, it maps to matching existing clusters at commit (unmatched names fall back to the default).</Li>
      </Ul>

      <H2>When to use it</H2>
      <Ul>
        <Li>A cluster's <C>currentArticleCount</C> is well below its <C>targetArticleCount</C> and you want fresh ideas.</Li>
        <Li>You've just enriched a few keywords and want to see what adjacent terms have proven volume.</Li>
        <Li>You suspect the cluster map needs a new cluster (e.g. you keep producing Ayurveda content under "Complementary Medicine" but feel it deserves its own).</Li>
      </Ul>

      <H2>Running an expansion</H2>

      <Step n={1} title="Hover the cluster card → click the ✨ button">
        <P>
          On the <AppLink to="pipeline">Pipeline</AppLink> page, the cluster strip at top has a sparkles icon that appears on hover. Clicking opens the cluster-expansion dialog.
        </P>
      </Step>

      <Step n={2} title="Click 'Find more'">
        <P>The orchestrator runs in this order:</P>
        <Ul>
          <Li><strong>Seed pick</strong> — picks 1-3 best seed keywords from the cluster (pillar first, then highest-priority members).</Li>
          <Li><strong>DFS related_keywords</strong> — hits DataForSEO for each seed, depth=2, limit=50. Returns terms with verified search volume + CPC + intent.</Li>
          <Li><strong>Dedupe</strong> — drops terms that already exist in this cluster + the seeds themselves.</Li>
          <Li><strong>LLM expander</strong> — Claude Sonnet picks 12 suggestions from the DFS list, slotting each into an intent + funnel stage + pillar candidacy with rationale.</Li>
          <Li><strong>Classifier</strong> — Haiku runs against the full cluster catalogue for each suggestion. Flags cross-cluster fits + new-cluster proposals.</Li>
        </Ul>
        <P>Total time: 30-90s. The dialog shows progress + telemetry when done.</P>
      </Step>

      <Step n={3} title="Review the suggestions">
        <P>Three tabs: <strong>Pending</strong> / <strong>Accepted</strong> / <strong>Rejected</strong>. Each pending row shows:</P>
        <Ul>
          <Li><strong>Provenance badge</strong> — DFS (orange) means the LLM picked a term verbatim from the DFS list (verified volume). AI (purple) means the LLM proposed it without DFS backing.</Li>
          <Li><strong>Search volume + CPC + KD</strong> when from DFS.</Li>
          <Li><strong>Intent + funnel stage + recommended priority</strong> from the LLM.</Li>
          <Li><strong>Pillar candidate</strong> badge when the LLM marked it as one.</Li>
          <Li><strong>Better fit: X (89%)</strong> yellow pill when the classifier picked a different cluster with ≥60% confidence. Click to move there with one click.</Li>
        </Ul>
      </Step>

      <Step n={4} title="Accept / Move / Reject">
        <Ul>
          <Li><strong>Accept</strong> — creates a real keyword row in this cluster, status=backlog, source tags include the provenance.</Li>
          <Li><strong>Move to ▾</strong> — drops the keyword into any other cluster instead.</Li>
          <Li><strong>Reject</strong> — sticks; future expansion runs on this cluster won't re-suggest the same term.</Li>
          <Li><strong>Reopen</strong> (from Rejected tab) — flips back to pending if you changed your mind.</Li>
          <Li><strong>Bulk actions</strong> — tick the checkbox on each row (or "Select all"), then use the bar above the list to Accept / Reject / Move all selected at once (Reopen on the Rejected tab). Runs them in sequence with an N/total progress counter — no more clicking through dozens one at a time.</Li>
        </Ul>
      </Step>

      <H2>Proposed new clusters</H2>
      <P>
        When the classifier finds NO existing cluster fits a suggestion AND it represents a genuinely new topical area, it produces a <C>suggested_new_cluster</C> blob with a proposed id, Hebrew name, English name, and rationale. The dialog groups these by proposed id at the top in a violet "Proposed new clusters" section.
      </P>
      <P>
        Click <strong>Approve cluster</strong> on a proposal to: create the cluster + accept all seeding suggestions into it as <C>new_cluster_seed</C>-tagged keywords. One click does both.
      </P>

      <H2>Re-running on the same cluster</H2>
      <P>
        Safe to re-run. The unique index on <C>(cluster_id, LOWER(keyword), language)</C> means existing suggestions get their metadata refreshed (newer DFS volume, fresh rationale) but the operator's review state (pending / accepted / rejected) is preserved. Rejected terms don't re-surface.
      </P>

      <H2>Limitations + when to skip DFS</H2>
      <P>
        For clusters with no good seed (no pillar keyword, no members), the LLM-only mode (<C>skipDfs: true</C> via the API) still produces creative proposals — but they're hallucinated rather than volume-verified. Cross-check with DataForSEO before promoting to backlog at scale.
      </P>
      <Callout kind="warn">
        Hebrew keyword quality from the LLM expander is sometimes off — it occasionally invents phrases no Israeli would actually search (e.g. "מדקר סיני קרוב אליי" instead of "מטפל בדיקור סיני באזור"). The DFS-first design avoids this for most suggestions, but always sanity-check unusual terms before accepting at scale.
      </Callout>
    </>
  );
}

export const guide: Guide = { meta, Body };
